import type {
  ApprovalDetail,
  ApprovalListItem,
  AttendanceCategoryListItem,
  AssignmentSubmissionResult,
  AssignmentUploadIntent,
  EmailIdentification,
  InvitationListItem,
  InvitationCodePreview,
  MemberDisplayListItem,
  MemberGroupDetail,
  MemberGroupSummary,
  MemberGroupTagListItem,
  MemberManagementListItem,
  MemberManagementService,
  MemberManagementViewModel,
  MemberSelectorListItem,
  MemberSelectorPurpose,
  PermissionGroupDetail,
  PermissionGroupSummary,
  ScheduleDetail,
  ScheduleDraftInput,
  ScheduleUpdateDraftInput,
  ScheduleListItem,
  WorkspaceContextInput
} from './appService';
import type { AssignmentDetail, AssignmentListItem } from '../assignment/view';
import { readCloudApiErrorMessage, readCloudJsonResponse } from './cloudApi';
import type {
  InvitationCode,
  JoinRequest,
  MemberGroup,
  PermissionKey,
  PermissionGroupMember,
  SeasonMembershipMemberGroup,
  SeasonMembershipMemberGroupTag
} from './model';
import type { CloudAuthSliceTokenStore } from './cloudAuthSliceService';

const CLOUD_MEMBER_MANAGEMENT_API_FAILED_MESSAGE = 'Cloud member-management API request failed';

export interface CloudMemberManagementServiceConfig {
  readonly apiBaseUrl: string;
  readonly fetch: (url: string, init?: RequestInit) => Promise<Response>;
  readonly tokenStore: CloudAuthSliceTokenStore;
}

interface AuthSessionResponse {
  readonly sessionToken: string;
}

interface CreateChoirResponse {
  readonly choir: {
    readonly choirId: string;
  };
}

interface ApiScheduleListItem {
  readonly scheduleId: string;
  readonly choirId: string;
  readonly seasonId: string;
  readonly title: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly location: string;
  readonly note: string;
  readonly status: 'active' | 'deleted';
  readonly deletedAt: string | null;
  readonly attendanceStatus: ScheduleListItem['attendanceStatus'];
  readonly canManage: boolean;
  readonly attendance:
    | { readonly enabled: false }
    | {
        readonly enabled: true;
        readonly attendanceEventId: string;
        readonly attendanceCategory: {
          readonly attendanceCategoryId: string;
          readonly name: string;
        };
        readonly participantMemberGroups: readonly {
          readonly memberGroupId: string;
          readonly name: string;
        }[];
        readonly onTimeReferenceMinutesFromStart: number;
        readonly lateReferenceMinutesFromStart: number;
      };
}

interface ApiScheduleDetail {
  readonly scheduleId: string;
  readonly choirId: string;
  readonly seasonId: string;
  readonly title: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly location: string;
  readonly note: string;
  readonly status: 'active' | 'deleted';
  readonly deletedAt: string | null;
  readonly attendanceStatus: ScheduleListItem['attendanceStatus'];
  readonly canManage: boolean;
  readonly attendance:
    | { readonly enabled: false }
    | (Extract<ApiScheduleListItem['attendance'], { readonly enabled: true }> & {
        readonly records: readonly ApiAttendanceRecord[];
        readonly currentUserRecord: ApiAttendanceRecord | null;
      });
}

type ApiScheduleAttendanceCategory = Extract<
  ApiScheduleListItem['attendance'],
  { readonly enabled: true }
>['attendanceCategory'];

interface ApiAttendanceCategory {
  readonly attendanceCategoryId: string;
  readonly name: string;
  readonly status: AttendanceCategoryListItem['status'];
  readonly displayOrder: number;
}

interface ApiAttendanceRecord {
  readonly attendanceRecordId: string;
  readonly seasonMembershipId: string;
  readonly nickname: string;
  readonly memberGroupNames: readonly string[];
  readonly status: 'pending' | 'on_time' | 'late' | 'absent';
  readonly selectedAt: string | null;
  readonly settledAt: string | null;
}

interface ApiMemberGroupOption {
  readonly memberGroupId: string;
  readonly name: string;
  readonly isDefault: boolean;
  readonly displayOrder: number;
}

interface ApiMemberDisplayItem {
  readonly displayMemberId: string;
  readonly nickname: string;
  readonly memberGroups: readonly { readonly name: string }[];
}

interface ApiMemberManagementItem {
  readonly seasonMembershipId: string;
  readonly nickname: string;
  readonly role: MemberManagementListItem['role'];
  readonly memberGroups: readonly { readonly name: string }[];
}

interface ApiMemberSelectorItem {
  readonly seasonMembershipId: string;
  readonly nickname: string;
  readonly memberGroups: readonly { readonly name: string }[];
}

interface ApiMemberGroupSummaryItem {
  readonly memberGroupId: string;
  readonly name: string;
  readonly isDefault: boolean;
  readonly memberCount: number;
  readonly canManage: boolean;
}

interface ApiMemberGroupDetail extends MemberGroupDetail {
  readonly choirId: string;
  readonly seasonId: string;
}

interface ApiPermissionGroupSummary {
  readonly permissionGroupId: string;
  readonly choirId: string;
  readonly seasonId: string;
  readonly name: string;
  readonly permissionKeys: readonly PermissionKey[];
  readonly memberCount: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

interface ApiPermissionGroupDetail {
  readonly permissionGroupId: string;
  readonly choirId: string;
  readonly seasonId: string;
  readonly name: string;
  readonly permissionKeys: readonly PermissionKey[];
  readonly members: readonly {
    readonly seasonMembershipId: string;
    readonly nickname: string;
    readonly memberGroups: readonly { readonly name: string }[];
  }[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

interface AssignmentSubmissionResponse {
  readonly submission: AssignmentSubmissionResult['submission'];
  readonly cleanupWarning: {
    readonly objectKey: string;
    readonly message: string;
  } | null;
}

interface ApiAssignmentCreateResponse {
  readonly assignment: ApiAssignmentDetailResponse['assignment'];
  readonly deliveryRules: AssignmentDetail['deliveryRules'];
}

interface ApiAssignmentDetailResponse {
  readonly assignment: {
    readonly assignmentId: string;
    readonly choirId: string;
    readonly seasonId: string;
    readonly pieceName: string;
    readonly dueAt: string;
    readonly submissionType: AssignmentDetail['submissionType'];
    readonly description: string;
    readonly status: AssignmentDetail['status'];
    readonly canManage: boolean;
  };
  readonly deliveryRules: AssignmentDetail['deliveryRules'];
  readonly mySubmission:
    | { readonly status: AssignmentListItem['mySubmissionStatus'] }
    | ({
        readonly status: AssignmentListItem['mySubmissionStatus'];
      } & NonNullable<AssignmentDetail['mySubmission']>);
  readonly publicSubmissionStatus: readonly {
    readonly seasonMembershipId: string;
    readonly nickname: string;
    readonly memberGroupNames: readonly string[];
    readonly status: AssignmentDetail['submissionRows'][number]['status'];
    readonly submittedAt: string | null;
  }[];
  readonly submissionEntry: {
    readonly canSubmit: boolean;
    readonly isOverwrite: boolean;
  };
}

export function createCloudMemberManagementService(
  config: CloudMemberManagementServiceConfig
): MemberManagementService {
  const apiBaseUrl = config.apiBaseUrl.replace(/\/+$/, '');
  let selectedChoirId: string | null | undefined;

  const request = async <T>(path: string, init: RequestInit & { readonly requireAuth?: boolean } = {}): Promise<T> => {
    const { requireAuth, headers: initHeaders, ...fetchInit } = init;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(initHeaders as Record<string, string> | undefined)
    };
    const token = config.tokenStore.get();

    if (requireAuth) {
      if (!token) {
        throw new Error('Cloud member-management API requires a session token');
      }

      headers.Authorization = `Bearer ${token}`;
    }

    const response = await config.fetch(`${apiBaseUrl}${path}`, {
      ...fetchInit,
      headers
    });
    const body = await readCloudJsonResponse(response, CLOUD_MEMBER_MANAGEMENT_API_FAILED_MESSAGE);

    if (!response.ok) {
      throw new Error(readCloudApiErrorMessage(body, CLOUD_MEMBER_MANAGEMENT_API_FAILED_MESSAGE));
    }

    return body as T;
  };

  const saveSession = (response: AuthSessionResponse): void => {
    config.tokenStore.set(response.sessionToken);
    selectedChoirId = undefined;
  };

  const setCurrentChoir = async (choirId: string | null): Promise<void> => {
    await request<{ readonly status: 'ok' }>('/api/me/current-choir', {
      method: 'PUT',
      body: JSON.stringify({ choirId }),
      requireAuth: true
    });
    selectedChoirId = choirId;
  };

  const requireAttendanceCategoryInWorkspace = async (
    workspaceContext: WorkspaceContextInput,
    attendanceCategoryId: string
  ): Promise<void> => {
    const categories = await service.listAttendanceCategories({ workspaceContext });
    if (!categories.some((category) => category.attendanceCategoryId === attendanceCategoryId)) {
      throw new Error('Attendance category belongs to a different workspace');
    }
  };

  const requireScheduleInWorkspaceBeforeMutation = async (
    workspaceContext: WorkspaceContextInput,
    scheduleId: string
  ): Promise<void> => {
    const detailResponse = await request<{ readonly schedule: ApiScheduleDetail }>(
      `/api/schedules/${encodeURIComponent(scheduleId)}`,
      {
        method: 'GET',
        requireAuth: true
      }
    );
    requireApiScheduleDetailForRequest(detailResponse.schedule, workspaceContext, scheduleId);
  };

  const readMemberGroupDetailInWorkspace = async (
    workspaceContext: WorkspaceContextInput,
    memberGroupId: string
  ): Promise<ApiMemberGroupDetail> => {
    const response = await request<{ readonly memberGroup: ApiMemberGroupDetail }>(
      `/api/member-groups/${encodeURIComponent(memberGroupId)}`,
      {
        method: 'GET',
        requireAuth: true
      }
    );

    return requireApiMemberGroupDetailForRequest(response.memberGroup, workspaceContext, memberGroupId);
  };

  const readPermissionGroupDetailInWorkspace = async (
    workspaceContext: WorkspaceContextInput,
    permissionGroupId: string
  ): Promise<ApiPermissionGroupDetail> => {
    const response = await request<{ readonly permissionGroup: ApiPermissionGroupDetail }>(
      `/api/permission-groups/${encodeURIComponent(permissionGroupId)}`,
      {
        method: 'GET',
        requireAuth: true
      }
    );

    return requireApiPermissionGroupDetailForRequest(response.permissionGroup, workspaceContext, permissionGroupId);
  };

  const requireMemberGroupTagInWorkspace = async (
    workspaceContext: WorkspaceContextInput,
    memberGroupId: string,
    memberGroupTagId: string
  ): Promise<void> => {
    const detail = await readMemberGroupDetailInWorkspace(workspaceContext, memberGroupId);
    if (!detail.tags.some((tag) => tag.memberGroupTagId === memberGroupTagId)) {
      throw new Error('Member group tag does not belong to the provided member group');
    }
  };

  const unsupportedResourceService = (method: string) => async (): Promise<never> => {
    throw new Error(`${method} is not implemented for cloud member-management service`);
  };

  const service: MemberManagementService = {
    identifyEmail: async (input): Promise<EmailIdentification> =>
      await request<EmailIdentification>('/api/auth/identify-email', {
        method: 'POST',
        body: JSON.stringify(input)
      }),

    register: async (input) => {
      saveSession(
        await request<AuthSessionResponse>('/api/auth/register', {
          method: 'POST',
          body: JSON.stringify(input)
        })
      );
    },

    login: async (input) => {
      saveSession(
        await request<AuthSessionResponse>('/api/auth/login', {
          method: 'POST',
          body: JSON.stringify(input)
        })
      );
    },

    logout: async () => {
      if (config.tokenStore.get()) {
        await request<{ readonly status: 'ok' }>('/api/auth/logout', {
          method: 'POST',
          requireAuth: true
        });
      }

      config.tokenStore.clear();
      selectedChoirId = undefined;
    },

    getViewModel: async (): Promise<MemberManagementViewModel> => {
      if (!config.tokenStore.get()) {
        return createSignedOutCloudViewModel();
      }

      return await request<MemberManagementViewModel>(
        `/api/member-management/view-model${toCurrentChoirQuery(selectedChoirId)}`,
        {
          method: 'GET',
          requireAuth: true
        }
      );
    },

    createChoir: async (input) => {
      const response = await request<CreateChoirResponse>('/api/choirs', {
        method: 'POST',
        body: JSON.stringify(input),
        requireAuth: true
      });
      await setCurrentChoir(response.choir.choirId);
    },

    switchChoir: async ({ choirId }) => {
      await setCurrentChoir(choirId);
    },

    leaveCurrentChoirWorkspace: async () => {
      await setCurrentChoir(null);
    },

    listMemberDisplayItems: async ({ workspaceContext }): Promise<readonly MemberDisplayListItem[]> => {
      const response = await request<{ readonly members: readonly ApiMemberDisplayItem[] }>(
        `/api/choirs/${encodeURIComponent(workspaceContext.currentChoirId)}/members?seasonId=current`,
        {
          method: 'GET',
          requireAuth: true
        }
      );

      return response.members.map((member) => ({
        displayMemberId: member.displayMemberId,
        nickname: member.nickname,
        memberGroupNames: member.memberGroups.map((memberGroup) => memberGroup.name)
      }));
    },

    listMemberManagementItems: async ({ workspaceContext }): Promise<readonly MemberManagementListItem[]> => {
      const response = await request<{ readonly members: readonly ApiMemberManagementItem[] }>(
        `/api/choirs/${encodeURIComponent(workspaceContext.currentChoirId)}/members?seasonId=current&view=management`,
        {
          method: 'GET',
          requireAuth: true
        }
      );

      return response.members.map((member) => ({
        seasonMembershipId: member.seasonMembershipId,
        nickname: member.nickname,
        role: member.role,
        memberGroupNames: member.memberGroups.map((memberGroup) => memberGroup.name)
      }));
    },

    listMemberSelectorItems: async ({ workspaceContext, purpose }): Promise<readonly MemberSelectorListItem[]> => {
      assertSupportedMemberSelectorPurpose(purpose);
      const response = await request<{ readonly members: readonly ApiMemberSelectorItem[] }>(
        `/api/choirs/${encodeURIComponent(
          workspaceContext.currentChoirId
        )}/members?seasonId=current&view=selector&purpose=${encodeURIComponent(purpose)}`,
        {
          method: 'GET',
          requireAuth: true
        }
      );

      return response.members.map((member) => ({
        seasonMembershipId: member.seasonMembershipId,
        nickname: member.nickname,
        memberGroupNames: member.memberGroups.map((memberGroup) => memberGroup.name)
      }));
    },

    listMemberGroupSummaries: async ({ workspaceContext }): Promise<readonly MemberGroupSummary[]> => {
      const response = await request<{
        readonly memberGroups: readonly ApiMemberGroupSummaryItem[];
      }>(`/api/choirs/${encodeURIComponent(workspaceContext.currentChoirId)}/member-groups?seasonId=current`, {
        method: 'GET',
        requireAuth: true
      });

      return response.memberGroups.map((memberGroup) => ({
        memberGroupId: memberGroup.memberGroupId,
        name: memberGroup.name,
        isDefault: memberGroup.isDefault,
        memberCount: memberGroup.memberCount,
        canManage: memberGroup.canManage
      }));
    },

    listPermissionGroups: async ({ workspaceContext }): Promise<readonly PermissionGroupSummary[]> => {
      const response = await request<{
        readonly permissionGroups: readonly ApiPermissionGroupSummary[];
      }>(`/api/choirs/${encodeURIComponent(workspaceContext.currentChoirId)}/permission-groups?seasonId=current`, {
        method: 'GET',
        requireAuth: true
      });

      return response.permissionGroups.map((permissionGroup) =>
        toFrontendPermissionGroupSummary(requireApiPermissionGroupSummaryInWorkspace(permissionGroup, workspaceContext))
      );
    },

    getPermissionGroupDetail: async ({ workspaceContext, permissionGroupId }): Promise<PermissionGroupDetail> => {
      return toFrontendPermissionGroupDetail(
        await readPermissionGroupDetailInWorkspace(workspaceContext, permissionGroupId)
      );
    },

    createPermissionGroup: async ({ workspaceContext, name, permissionKeys }): Promise<PermissionGroupSummary> => {
      const response = await request<{ readonly permissionGroup: ApiPermissionGroupSummary }>(
        `/api/choirs/${encodeURIComponent(workspaceContext.currentChoirId)}/permission-groups`,
        {
          method: 'POST',
          body: JSON.stringify({ name, permissionKeys }),
          requireAuth: true
        }
      );

      return toFrontendPermissionGroupSummary(
        requireApiPermissionGroupSummaryInWorkspace(response.permissionGroup, workspaceContext)
      );
    },

    updatePermissionGroup: async ({
      workspaceContext,
      permissionGroupId,
      name,
      permissionKeys
    }): Promise<PermissionGroupSummary> => {
      await readPermissionGroupDetailInWorkspace(workspaceContext, permissionGroupId);
      const response = await request<{ readonly permissionGroup: ApiPermissionGroupDetail }>(
        `/api/permission-groups/${encodeURIComponent(permissionGroupId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ name, permissionKeys }),
          requireAuth: true
        }
      );

      return toFrontendPermissionGroupSummaryFromDetail(
        requireApiPermissionGroupDetailForRequest(response.permissionGroup, workspaceContext, permissionGroupId)
      );
    },

    deletePermissionGroup: async ({ workspaceContext, permissionGroupId }) => {
      await readPermissionGroupDetailInWorkspace(workspaceContext, permissionGroupId);
      const response = await request<{ readonly permissionGroup: ApiPermissionGroupDetail }>(
        `/api/permission-groups/${encodeURIComponent(permissionGroupId)}`,
        {
          method: 'DELETE',
          requireAuth: true
        }
      );
      requireApiPermissionGroupDetailForRequest(response.permissionGroup, workspaceContext, permissionGroupId);
    },

    addPermissionGroupMember: async ({ workspaceContext, permissionGroupId, seasonMembershipId }) => {
      await readPermissionGroupDetailInWorkspace(workspaceContext, permissionGroupId);
      await request<{ readonly permissionGroupMember: PermissionGroupMember }>(
        `/api/permission-groups/${encodeURIComponent(permissionGroupId)}/members`,
        {
          method: 'POST',
          body: JSON.stringify({ seasonMembershipId }),
          requireAuth: true
        }
      );
    },

    removePermissionGroupMember: async ({ workspaceContext, permissionGroupId, seasonMembershipId }) => {
      await readPermissionGroupDetailInWorkspace(workspaceContext, permissionGroupId);
      await request<{ readonly permissionGroupMember: PermissionGroupMember }>(
        `/api/permission-groups/${encodeURIComponent(permissionGroupId)}/members/${encodeURIComponent(
          seasonMembershipId
        )}`,
        {
          method: 'DELETE',
          requireAuth: true
        }
      );
    },

    listInvitations: async ({ workspaceContext }): Promise<readonly InvitationListItem[]> => {
      const response = await request<{ readonly invitationCodes: readonly InvitationCode[] }>(
        `/api/choirs/${encodeURIComponent(workspaceContext.currentChoirId)}/invitations`,
        {
          method: 'GET',
          requireAuth: true
        }
      );

      return response.invitationCodes.map((invitationCode) => ({
        invitationCodeId: invitationCode.invitationCodeId,
        code: invitationCode.code,
        status: invitationCode.status,
        createdAt: invitationCode.createdAt,
        expiresAt: invitationCode.expiresAt
      }));
    },

    listApprovals: async ({ workspaceContext }): Promise<readonly ApprovalListItem[]> => {
      const response = await request<{ readonly joinRequests: readonly ApprovalListItem[] }>(
        `/api/choirs/${encodeURIComponent(workspaceContext.currentChoirId)}/join-requests?status=pending`,
        {
          method: 'GET',
          requireAuth: true
        }
      );

      return response.joinRequests.map((joinRequest) => ({
        joinRequestId: joinRequest.joinRequestId,
        nickname: joinRequest.nickname,
        applicantEmail: joinRequest.applicantEmail,
        submittedAt: joinRequest.submittedAt
      }));
    },

    getApprovalDetail: async ({ workspaceContext, joinRequestId }): Promise<ApprovalDetail> => {
      const response = await request<{ readonly approval: ApprovalDetail }>(
        `/api/choirs/${encodeURIComponent(
          workspaceContext.currentChoirId
        )}/join-requests/${encodeURIComponent(joinRequestId)}`,
        {
          method: 'GET',
          requireAuth: true
        }
      );

      return {
        joinRequestId: response.approval.joinRequestId,
        nickname: response.approval.nickname,
        applicantEmail: response.approval.applicantEmail,
        submittedAt: response.approval.submittedAt,
        memberGroupChoices: response.approval.memberGroupChoices.map((group) => ({
          memberGroupId: group.memberGroupId,
          name: group.name,
          isDefault: group.isDefault,
          displayOrder: group.displayOrder
        }))
      };
    },

    generateInvitationCode: async ({ workspaceContext }): Promise<InvitationCode> => {
      const response = await request<{ readonly invitationCode: InvitationCode }>(
        `/api/choirs/${encodeURIComponent(workspaceContext.currentChoirId)}/invitations`,
        {
          method: 'POST',
          requireAuth: true
        }
      );

      return response.invitationCode;
    },

    previewInvitationCode: async (input): Promise<InvitationCodePreview> =>
      await request<InvitationCodePreview>('/api/invitations/resolve', {
        method: 'POST',
        body: JSON.stringify(input),
        requireAuth: true
      }),

    deleteInvitationCode: async ({ workspaceContext, invitationCodeId }): Promise<InvitationCode> => {
      const response = await request<{ readonly invitationCode: InvitationCode }>(
        `/api/invitations/${encodeURIComponent(invitationCodeId)}`,
        {
          method: 'DELETE',
          requireAuth: true
        }
      );

      return requireInvitationCodeInWorkspace(response.invitationCode, workspaceContext);
    },

    submitJoinRequest: async (input): Promise<JoinRequest> => {
      const response = await request<{ readonly joinRequest: JoinRequest }>('/api/join-requests', {
        method: 'POST',
        body: JSON.stringify(input),
        requireAuth: true
      });

      return response.joinRequest;
    },

    approveJoinRequest: async ({ workspaceContext, joinRequestId, memberGroupIds }) => {
      const response = await request<{ readonly joinRequest: JoinRequest }>(
        `/api/choirs/${encodeURIComponent(
          workspaceContext.currentChoirId
        )}/join-requests/${encodeURIComponent(joinRequestId)}/approve`,
        {
          method: 'POST',
          body: JSON.stringify({ memberGroupIds }),
          requireAuth: true
        }
      );
      requireJoinRequestInWorkspace(response.joinRequest, workspaceContext);
    },

    rejectJoinRequest: async ({ workspaceContext, joinRequestId, rejectionReason }) => {
      const response = await request<{ readonly joinRequest: JoinRequest }>(
        `/api/choirs/${encodeURIComponent(
          workspaceContext.currentChoirId
        )}/join-requests/${encodeURIComponent(joinRequestId)}/reject`,
        {
          method: 'POST',
          body: JSON.stringify({ rejectionReason }),
          requireAuth: true
        }
      );
      requireJoinRequestInWorkspace(response.joinRequest, workspaceContext);
    },

    createMemberGroup: async ({ workspaceContext, name, initialSeasonMembershipId }): Promise<MemberGroupSummary> => {
      const response = await request<{
        readonly memberGroup: MemberGroup;
        readonly memberGroupSummary: MemberGroupSummary;
      }>(`/api/choirs/${encodeURIComponent(workspaceContext.currentChoirId)}/member-groups`, {
        method: 'POST',
        body: JSON.stringify({ name }),
        requireAuth: true
      });
      requireMemberGroupInWorkspace(response.memberGroup, workspaceContext);

      if (initialSeasonMembershipId) {
        await service.addMemberGroupMember({
          workspaceContext,
          memberGroupId: response.memberGroup.memberGroupId,
          seasonMembershipId: initialSeasonMembershipId
        });
        const detail = await readMemberGroupDetailInWorkspace(workspaceContext, response.memberGroup.memberGroupId);

        return toMemberGroupSummaryFromDetail(detail);
      }

      return response.memberGroupSummary;
    },

    renameMemberGroup: async ({ workspaceContext, memberGroupId, name }) => {
      await readMemberGroupDetailInWorkspace(workspaceContext, memberGroupId);
      await request(`/api/member-groups/${encodeURIComponent(memberGroupId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
        requireAuth: true
      });
    },

    deleteMemberGroup: async ({ workspaceContext, memberGroupId }) => {
      await readMemberGroupDetailInWorkspace(workspaceContext, memberGroupId);
      await request(`/api/member-groups/${encodeURIComponent(memberGroupId)}`, {
        method: 'DELETE',
        requireAuth: true
      });
    },

    addMemberGroupMember: async ({ workspaceContext, memberGroupId, seasonMembershipId }) => {
      await readMemberGroupDetailInWorkspace(workspaceContext, memberGroupId);
      await request<{ readonly memberGroupMember: SeasonMembershipMemberGroup }>(
        `/api/member-groups/${encodeURIComponent(memberGroupId)}/members`,
        {
          method: 'POST',
          body: JSON.stringify({ seasonMembershipId }),
          requireAuth: true
        }
      );
    },

    removeMemberGroupMember: async ({ workspaceContext, memberGroupId, seasonMembershipId }) => {
      await readMemberGroupDetailInWorkspace(workspaceContext, memberGroupId);
      await request<{ readonly memberGroupMember: SeasonMembershipMemberGroup }>(
        `/api/member-groups/${encodeURIComponent(memberGroupId)}/members/${encodeURIComponent(seasonMembershipId)}`,
        {
          method: 'DELETE',
          requireAuth: true
        }
      );
    },

    setMemberGroupManagerGrant: async ({ workspaceContext, memberGroupId, seasonMembershipId, enabled }) => {
      await readMemberGroupDetailInWorkspace(workspaceContext, memberGroupId);
      await request(
        enabled
          ? `/api/member-groups/${encodeURIComponent(memberGroupId)}/managers`
          : `/api/member-groups/${encodeURIComponent(memberGroupId)}/managers/${encodeURIComponent(
              seasonMembershipId
            )}`,
        enabled
          ? {
              method: 'POST',
              body: JSON.stringify({ seasonMembershipId }),
              requireAuth: true
            }
          : {
              method: 'DELETE',
              requireAuth: true
            }
      );
    },

    createMemberGroupTag: async ({ workspaceContext, memberGroupId, name }): Promise<MemberGroupTagListItem> => {
      await readMemberGroupDetailInWorkspace(workspaceContext, memberGroupId);
      const response = await request<{
        readonly memberGroupTag: {
          readonly memberGroupTagId: string;
          readonly name: string;
        };
      }>(`/api/member-groups/${encodeURIComponent(memberGroupId)}/tags`, {
        method: 'POST',
        body: JSON.stringify({ name }),
        requireAuth: true
      });

      return {
        memberGroupTagId: response.memberGroupTag.memberGroupTagId,
        name: response.memberGroupTag.name,
        memberCount: 0
      };
    },

    renameMemberGroupTag: async ({ workspaceContext, memberGroupId, memberGroupTagId, name }) => {
      await requireMemberGroupTagInWorkspace(workspaceContext, memberGroupId, memberGroupTagId);
      await request(`/api/member-group-tags/${encodeURIComponent(memberGroupTagId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
        requireAuth: true
      });
    },

    deleteMemberGroupTag: async ({ workspaceContext, memberGroupId, memberGroupTagId }) => {
      await requireMemberGroupTagInWorkspace(workspaceContext, memberGroupId, memberGroupTagId);
      await request(`/api/member-group-tags/${encodeURIComponent(memberGroupTagId)}`, {
        method: 'DELETE',
        requireAuth: true
      });
    },

    setMemberGroupTagMembership: async ({
      workspaceContext,
      memberGroupId,
      memberGroupTagId,
      seasonMembershipId,
      enabled
    }) => {
      await requireMemberGroupTagInWorkspace(workspaceContext, memberGroupId, memberGroupTagId);
      await request<{ readonly memberGroupMemberTag: SeasonMembershipMemberGroupTag }>(
        enabled
          ? `/api/member-group-tags/${encodeURIComponent(memberGroupTagId)}/members`
          : `/api/member-group-tags/${encodeURIComponent(
              memberGroupTagId
            )}/members/${encodeURIComponent(seasonMembershipId)}`,
        enabled
          ? {
              method: 'POST',
              body: JSON.stringify({ seasonMembershipId }),
              requireAuth: true
            }
          : {
              method: 'DELETE',
              requireAuth: true
            }
      );
    },

    getMemberGroupDetail: async ({ workspaceContext, memberGroupId }): Promise<MemberGroupDetail> => {
      return toFrontendMemberGroupDetail(await readMemberGroupDetailInWorkspace(workspaceContext, memberGroupId));
    },

    listSchedules: async ({ workspaceContext, from, to }): Promise<readonly ScheduleListItem[]> => {
      const response = await request<{ readonly schedules: readonly ApiScheduleListItem[] }>(
        `/api/choirs/${encodeURIComponent(
          workspaceContext.currentChoirId
        )}/schedules?seasonId=current&from=${encodeURIComponent(from)}&to=${encodeURIComponent(
          to
        )}&includeDeleted=false`,
        {
          method: 'GET',
          requireAuth: true
        }
      );

      return response.schedules.map((schedule) =>
        toFrontendScheduleListItem(requireApiScheduleInWorkspace(schedule, workspaceContext))
      );
    },

    listUpcomingSchedules: async ({ workspaceContext, limit }): Promise<readonly ScheduleListItem[]> => {
      const response = await request<{ readonly schedules: readonly ApiScheduleListItem[] }>(
        `/api/choirs/${encodeURIComponent(
          workspaceContext.currentChoirId
        )}/schedules/upcoming?seasonId=current&limit=${encodeURIComponent(String(limit))}`,
        {
          method: 'GET',
          requireAuth: true
        }
      );

      return response.schedules.map((schedule) =>
        toFrontendScheduleListItem(requireApiScheduleInWorkspace(schedule, workspaceContext))
      );
    },

    getScheduleDetail: async ({ workspaceContext, scheduleId }): Promise<ScheduleDetail> => {
      const response = await request<{ readonly schedule: ApiScheduleDetail }>(
        `/api/schedules/${encodeURIComponent(scheduleId)}`,
        {
          method: 'GET',
          requireAuth: true
        }
      );

      return toFrontendScheduleDetail(
        requireApiScheduleDetailForRequest(response.schedule, workspaceContext, scheduleId)
      );
    },

    createSchedule: async ({ workspaceContext, draft }): Promise<ScheduleDetail> => {
      assertScheduleAttendanceIntentExplicit(draft);
      const response = await request<{ readonly schedule: ApiScheduleDetail }>(
        `/api/choirs/${encodeURIComponent(workspaceContext.currentChoirId)}/schedules`,
        {
          method: 'POST',
          body: JSON.stringify(toApiScheduleDraft(draft)),
          requireAuth: true
        }
      );

      return toFrontendScheduleDetail(requireApiScheduleInWorkspace(response.schedule, workspaceContext));
    },

    updateSchedule: async ({ workspaceContext, scheduleId, patch }): Promise<ScheduleDetail> => {
      await requireScheduleInWorkspaceBeforeMutation(workspaceContext, scheduleId);
      const response = await request<{ readonly schedule: ApiScheduleDetail }>(
        `/api/schedules/${encodeURIComponent(scheduleId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify(toApiScheduleDraft(patch)),
          requireAuth: true
        }
      );

      return toFrontendScheduleDetail(
        requireApiScheduleDetailForRequest(response.schedule, workspaceContext, scheduleId)
      );
    },

    deleteSchedule: async ({ workspaceContext, scheduleId }) => {
      await requireScheduleInWorkspaceBeforeMutation(workspaceContext, scheduleId);
      await request<{ readonly schedule: { readonly scheduleId: string } }>(
        `/api/schedules/${encodeURIComponent(scheduleId)}`,
        {
          method: 'DELETE',
          requireAuth: true
        }
      );
    },

    getHomeUpcomingSchedule: async ({ workspaceContext }): Promise<ScheduleListItem | null> => {
      const response = await request<{ readonly schedule: ApiScheduleListItem | null }>(
        `/api/choirs/${encodeURIComponent(workspaceContext.currentChoirId)}/schedules/home-upcoming?seasonId=current`,
        {
          method: 'GET',
          requireAuth: true
        }
      );

      return response.schedule
        ? toFrontendScheduleListItem(requireApiScheduleInWorkspace(response.schedule, workspaceContext))
        : null;
    },

    getScheduleEditOptions: async ({ workspaceContext }) => {
      const [attendanceCategories, memberGroupsResponse] = await Promise.all([
        service.listAttendanceCategories({ workspaceContext }),
        request<{ readonly memberGroups: readonly ApiMemberGroupOption[] }>(
          `/api/choirs/${encodeURIComponent(workspaceContext.currentChoirId)}/member-groups?seasonId=current`,
          {
            method: 'GET',
            requireAuth: true
          }
        )
      ]);

      return {
        attendanceCategories: attendanceCategories.filter((category) => category.status === 'enabled'),
        participantMemberGroups: memberGroupsResponse.memberGroups.map((memberGroup) => ({
          memberGroupId: memberGroup.memberGroupId,
          name: memberGroup.name,
          isDefault: memberGroup.isDefault,
          displayOrder: memberGroup.displayOrder
        }))
      };
    },

    listAttendanceCategories: async ({ workspaceContext }): Promise<readonly AttendanceCategoryListItem[]> => {
      const response = await request<{
        readonly attendanceCategories: readonly ApiAttendanceCategory[];
      }>(`/api/choirs/${encodeURIComponent(workspaceContext.currentChoirId)}/attendance-categories?seasonId=current`, {
        method: 'GET',
        requireAuth: true
      });

      return response.attendanceCategories;
    },

    createAttendanceCategory: async ({ workspaceContext, name }): Promise<AttendanceCategoryListItem> => {
      const response = await request<{ readonly attendanceCategory: ApiAttendanceCategory }>(
        `/api/choirs/${encodeURIComponent(workspaceContext.currentChoirId)}/attendance-categories`,
        {
          method: 'POST',
          body: JSON.stringify({ name }),
          requireAuth: true
        }
      );

      return response.attendanceCategory;
    },

    renameAttendanceCategory: async ({
      workspaceContext,
      attendanceCategoryId,
      name
    }): Promise<AttendanceCategoryListItem> => {
      await requireAttendanceCategoryInWorkspace(workspaceContext, attendanceCategoryId);
      const response = await request<{ readonly attendanceCategory: ApiAttendanceCategory }>(
        `/api/attendance-categories/${encodeURIComponent(attendanceCategoryId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ name }),
          requireAuth: true
        }
      );

      return response.attendanceCategory;
    },

    disableAttendanceCategory: async ({
      workspaceContext,
      attendanceCategoryId
    }): Promise<AttendanceCategoryListItem> => {
      await requireAttendanceCategoryInWorkspace(workspaceContext, attendanceCategoryId);
      const response = await request<{ readonly attendanceCategory: ApiAttendanceCategory }>(
        `/api/attendance-categories/${encodeURIComponent(attendanceCategoryId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ status: 'disabled' }),
          requireAuth: true
        }
      );

      return response.attendanceCategory;
    },

    signAttendance: async ({ workspaceContext, scheduleId, status }): Promise<ScheduleDetail> => {
      await requireScheduleInWorkspaceBeforeMutation(workspaceContext, scheduleId);
      const response = await request<{
        readonly attendanceRecord: {
          readonly attendanceRecordId: string;
          readonly status: 'on_time' | 'late';
          readonly selectedAt: string | null;
          readonly settledAt: string | null;
        };
        readonly schedule: ApiScheduleDetail;
      }>(`/api/schedules/${encodeURIComponent(scheduleId)}/attendance/sign-in`, {
        method: 'POST',
        body: JSON.stringify({ status }),
        requireAuth: true
      });

      return toFrontendScheduleDetail(
        requireApiScheduleDetailForRequest(response.schedule, workspaceContext, scheduleId)
      );
    },
    listAssignments: async ({ workspaceContext }): Promise<readonly AssignmentListItem[]> => {
      const response = await request<{ readonly assignments: readonly AssignmentListItem[] }>(
        `/api/choirs/${encodeURIComponent(workspaceContext.currentChoirId)}/assignments?seasonId=current`,
        {
          method: 'GET',
          requireAuth: true
        }
      );

      return response.assignments;
    },

    createAssignment: async ({ workspaceContext, draft }): Promise<AssignmentDetail> => {
      const response = await request<ApiAssignmentCreateResponse>(
        `/api/choirs/${encodeURIComponent(workspaceContext.currentChoirId)}/assignments`,
        {
          method: 'POST',
          body: JSON.stringify(draft),
          requireAuth: true
        }
      );

      return toFrontendAssignmentCreateDetail(response, workspaceContext);
    },

    getAssignmentDetail: async ({ workspaceContext, assignmentId }): Promise<AssignmentDetail> => {
      const response = await request<ApiAssignmentDetailResponse>(
        `/api/assignments/${encodeURIComponent(assignmentId)}`,
        {
          method: 'GET',
          requireAuth: true
        }
      );

      return toFrontendAssignmentDetail(response, workspaceContext);
    },

    replaceAssignmentDeliveryRules: async ({
      workspaceContext,
      assignmentId,
      deliveryRules
    }): Promise<AssignmentDetail> => {
      await request<{
        readonly assignment: {
          readonly assignmentId: string;
          readonly status: AssignmentDetail['status'];
          readonly canManage: boolean;
        };
        readonly deliveryRules: AssignmentDetail['deliveryRules'];
      }>(`/api/assignments/${encodeURIComponent(assignmentId)}/delivery-rules`, {
        method: 'PUT',
        body: JSON.stringify({ deliveryRules }),
        requireAuth: true
      });

      return await service.getAssignmentDetail({ workspaceContext, assignmentId });
    },

    createUploadIntent: async (input): Promise<AssignmentUploadIntent> => {
      const response = await request<{
        readonly fileUploadId: string;
        readonly objectKey: string;
        readonly method: AssignmentUploadIntent['upload']['method'];
        readonly url: string;
        readonly headers: Record<string, string>;
        readonly expiresAt: string;
      }>('/api/files/upload-intents', {
        method: 'POST',
        body: JSON.stringify(input),
        requireAuth: true
      });

      return {
        fileUploadId: response.fileUploadId,
        objectKey: response.objectKey,
        upload: {
          method: response.method,
          url: response.url,
          headers: response.headers,
          expiresAt: response.expiresAt
        }
      };
    },

    uploadAssignmentFile: async ({ upload, file }): Promise<void> => {
      let response: Response;
      try {
        response = await config.fetch(upload.url, {
          method: upload.method,
          headers: upload.headers,
          body: file
        });
      } catch {
        throw new Error('Assignment file upload failed');
      }

      if (!response.ok) {
        throw new Error('Assignment file upload failed');
      }
    },

    completeUpload: async ({ fileUploadId, objectKey, sizeBytes, etag }): Promise<void> => {
      await request('/api/files/upload-complete', {
        method: 'POST',
        body: JSON.stringify({ fileUploadId, objectKey, sizeBytes, etag }),
        requireAuth: true
      });
    },

    submitMyAssignment: async ({ assignmentId, fileUploadId, note }): Promise<AssignmentSubmissionResult> => {
      const response = await request<AssignmentSubmissionResponse>(
        `/api/assignments/${encodeURIComponent(assignmentId)}/my-submission`,
        {
          method: 'PUT',
          body: JSON.stringify({ fileUploadId, note }),
          requireAuth: true
        }
      );

      return {
        submission: {
          assignmentSubmissionId: response.submission.assignmentSubmissionId,
          submittedAt: response.submission.submittedAt,
          fileName: response.submission.fileName
        },
        cleanupWarning: toFrontendCleanupWarning(response.cleanupWarning)
      };
    },

    getAssignmentSubmissionAccess: async ({
      assignmentSubmissionId
    }): Promise<{ readonly url: string; readonly expiresAt: string }> => {
      const response = await request<{
        readonly url: string;
        readonly expiresAt: string;
      }>(`/api/assignment-submissions/${encodeURIComponent(assignmentSubmissionId)}/access`, {
        method: 'GET',
        requireAuth: true
      });

      return {
        url: response.url,
        expiresAt: response.expiresAt
      };
    },

    getResourcesRoot: unsupportedResourceService('getResourcesRoot'),
    createResourceFolder: unsupportedResourceService('createResourceFolder'),
    createSong: unsupportedResourceService('createSong'),
    listSongs: unsupportedResourceService('listSongs'),
    getSongDetail: unsupportedResourceService('getSongDetail'),
    createSongRelatedFolder: unsupportedResourceService('createSongRelatedFolder'),
    createResourceUploadIntent: unsupportedResourceService('createResourceUploadIntent'),
    uploadResourceFile: unsupportedResourceService('uploadResourceFile'),
    attachOrdinaryResourceFile: unsupportedResourceService('attachOrdinaryResourceFile'),
    attachSongRelatedResourceFile: unsupportedResourceService('attachSongRelatedResourceFile'),
    updateSongCurrentScore: unsupportedResourceService('updateSongCurrentScore'),
    getResourceFileAccess: unsupportedResourceService('getResourceFileAccess'),
    readMarkdownResourceFile: unsupportedResourceService('readMarkdownResourceFile'),
    listNotifications: unsupportedResourceService('listNotifications'),
    getUnreadNotificationState: unsupportedResourceService('getUnreadNotificationState'),
    markNotificationRead: unsupportedResourceService('markNotificationRead')
  };

  return service;
}

function toCurrentChoirQuery(choirId: string | null | undefined): string {
  if (choirId === undefined) {
    return '';
  }

  if (choirId === null) {
    return '?choirId=';
  }

  return `?choirId=${encodeURIComponent(choirId)}`;
}

function createSignedOutCloudViewModel(): MemberManagementViewModel {
  return {
    currentUser: null,
    currentChoir: null,
    currentMembership: null,
    currentSeason: null,
    isCurrentUserAdmin: false,
    currentPermissionKeys: [],
    joinedChoirs: [],
    memberGroups: [],
    memberGroupSummaries: [],
    members: [],
    availableInvitationCodes: [],
    pendingJoinRequests: [],
    ownJoinRequests: [],
    schedules: [],
    attendanceCategories: []
  };
}

function toApiScheduleDraft(draft: ScheduleDraftInput | ScheduleUpdateDraftInput) {
  const body: {
    title?: string;
    startsAt?: string;
    endsAt?: string;
    location?: string;
    note?: string;
    attendance?:
      | { readonly enabled: false }
      | {
          readonly enabled: true;
          readonly attendanceCategoryId: string;
          readonly participantMemberGroupIds: readonly string[];
          readonly onTimeReferenceMinutesFromStart: number;
          readonly lateReferenceMinutesFromStart: number;
        };
  } = {};

  if (draft.title !== undefined) {
    body.title = draft.title;
  }

  if (draft.startsAt !== undefined) {
    body.startsAt = draft.startsAt;
  }

  if (draft.endsAt !== undefined) {
    body.endsAt = draft.endsAt;
  }

  if (draft.location !== undefined) {
    body.location = draft.location;
  }

  if (draft.note !== undefined) {
    body.note = draft.note;
  }

  if (draft.attendance === null) {
    body.attendance = { enabled: false };
  } else if (draft.attendance === undefined) {
    return body;
  } else {
    body.attendance = {
      enabled: true,
      attendanceCategoryId: draft.attendance.attendanceCategoryId,
      participantMemberGroupIds: draft.attendance.participantMemberGroupIds,
      onTimeReferenceMinutesFromStart: draft.attendance.onTimeReferenceMinutesFromStart,
      lateReferenceMinutesFromStart: draft.attendance.lateReferenceMinutesFromStart
    };
  }

  return body;
}

function assertScheduleAttendanceIntentExplicit(draft: ScheduleDraftInput): void {
  if (!Object.prototype.hasOwnProperty.call(draft, 'attendance') || draft.attendance === undefined) {
    throw new Error('Schedule attendance must be explicit');
  }
}

function requireApiScheduleInWorkspace<T extends ApiScheduleListItem | ApiScheduleDetail>(
  schedule: T,
  workspaceContext: WorkspaceContextInput
): T {
  if (schedule.choirId !== workspaceContext.currentChoirId || schedule.seasonId !== workspaceContext.currentSeasonId) {
    throw new Error('Schedule detail belongs to a different workspace');
  }

  return schedule;
}

function requireInvitationCodeInWorkspace(
  invitationCode: InvitationCode,
  workspaceContext: WorkspaceContextInput
): InvitationCode {
  if (
    invitationCode.choirId !== workspaceContext.currentChoirId ||
    invitationCode.seasonId !== workspaceContext.currentSeasonId
  ) {
    throw new Error('Invitation code belongs to a different workspace');
  }

  return invitationCode;
}

function requireJoinRequestInWorkspace(joinRequest: JoinRequest, workspaceContext: WorkspaceContextInput): JoinRequest {
  if (
    joinRequest.choirId !== workspaceContext.currentChoirId ||
    joinRequest.seasonId !== workspaceContext.currentSeasonId
  ) {
    throw new Error('Join request belongs to a different workspace');
  }

  return joinRequest;
}

function requireMemberGroupInWorkspace(memberGroup: MemberGroup, workspaceContext: WorkspaceContextInput): MemberGroup {
  if (
    memberGroup.choirId !== workspaceContext.currentChoirId ||
    memberGroup.seasonId !== workspaceContext.currentSeasonId
  ) {
    throw new Error('Member group belongs to a different workspace');
  }

  return memberGroup;
}

function requireApiMemberGroupDetailForRequest(
  memberGroup: ApiMemberGroupDetail,
  workspaceContext: WorkspaceContextInput,
  memberGroupId: string
): ApiMemberGroupDetail {
  if (memberGroup.memberGroupId !== memberGroupId) {
    throw new Error('Malformed member group detail response');
  }

  if (
    memberGroup.choirId !== workspaceContext.currentChoirId ||
    memberGroup.seasonId !== workspaceContext.currentSeasonId
  ) {
    throw new Error('Member group belongs to a different workspace');
  }

  return memberGroup;
}

function requireApiPermissionGroupSummaryInWorkspace(
  permissionGroup: ApiPermissionGroupSummary,
  workspaceContext: WorkspaceContextInput
): ApiPermissionGroupSummary {
  if (
    permissionGroup.choirId !== workspaceContext.currentChoirId ||
    permissionGroup.seasonId !== workspaceContext.currentSeasonId
  ) {
    throw new Error('Permission group belongs to a different workspace');
  }

  return permissionGroup;
}

function requireApiPermissionGroupDetailForRequest(
  permissionGroup: ApiPermissionGroupDetail,
  workspaceContext: WorkspaceContextInput,
  permissionGroupId: string
): ApiPermissionGroupDetail {
  if (permissionGroup.permissionGroupId !== permissionGroupId) {
    throw new Error('Malformed permission group detail response');
  }

  if (
    permissionGroup.choirId !== workspaceContext.currentChoirId ||
    permissionGroup.seasonId !== workspaceContext.currentSeasonId
  ) {
    throw new Error('Permission group belongs to a different workspace');
  }

  return permissionGroup;
}

function toFrontendMemberGroupDetail(memberGroup: ApiMemberGroupDetail): MemberGroupDetail {
  return {
    memberGroupId: memberGroup.memberGroupId,
    name: memberGroup.name,
    isDefault: memberGroup.isDefault,
    memberCount: memberGroup.memberCount,
    canManage: memberGroup.canManage,
    canAddMembers: memberGroup.canAddMembers,
    members: memberGroup.members,
    availableMembersToAdd: memberGroup.availableMembersToAdd,
    tags: memberGroup.tags
  };
}

function toFrontendPermissionGroupSummary(permissionGroup: ApiPermissionGroupSummary): PermissionGroupSummary {
  return {
    permissionGroupId: permissionGroup.permissionGroupId,
    name: permissionGroup.name,
    permissionKeys: permissionGroup.permissionKeys,
    memberCount: permissionGroup.memberCount,
    createdAt: permissionGroup.createdAt,
    updatedAt: permissionGroup.updatedAt
  };
}

function toFrontendPermissionGroupSummaryFromDetail(permissionGroup: ApiPermissionGroupDetail): PermissionGroupSummary {
  return {
    permissionGroupId: permissionGroup.permissionGroupId,
    name: permissionGroup.name,
    permissionKeys: permissionGroup.permissionKeys,
    memberCount: permissionGroup.members.length,
    createdAt: permissionGroup.createdAt,
    updatedAt: permissionGroup.updatedAt
  };
}

function toFrontendPermissionGroupDetail(permissionGroup: ApiPermissionGroupDetail): PermissionGroupDetail {
  return {
    permissionGroupId: permissionGroup.permissionGroupId,
    name: permissionGroup.name,
    permissionKeys: permissionGroup.permissionKeys,
    members: permissionGroup.members.map((member) => ({
      seasonMembershipId: member.seasonMembershipId,
      nickname: member.nickname,
      memberGroups: member.memberGroups.map((memberGroup) => memberGroup.name)
    })),
    createdAt: permissionGroup.createdAt,
    updatedAt: permissionGroup.updatedAt
  };
}

function toMemberGroupSummaryFromDetail(memberGroup: ApiMemberGroupDetail): MemberGroupSummary {
  return {
    memberGroupId: memberGroup.memberGroupId,
    name: memberGroup.name,
    isDefault: memberGroup.isDefault,
    memberCount: memberGroup.memberCount,
    canManage: memberGroup.canManage
  };
}

function requireApiScheduleDetailForRequest(
  schedule: ApiScheduleDetail,
  workspaceContext: WorkspaceContextInput,
  scheduleId: string
): ApiScheduleDetail {
  requireApiScheduleInWorkspace(schedule, workspaceContext);

  if (schedule.scheduleId !== scheduleId) {
    throw new Error('Malformed schedule detail response');
  }

  return schedule;
}

function toFrontendScheduleListItem(schedule: ApiScheduleListItem): ScheduleListItem {
  return {
    scheduleId: schedule.scheduleId,
    title: schedule.title,
    startsAt: schedule.startsAt,
    endsAt: schedule.endsAt,
    location: schedule.location,
    note: schedule.note,
    attendanceCategoryName: schedule.attendance.enabled ? schedule.attendance.attendanceCategory.name : null,
    attendanceStatus: schedule.attendanceStatus,
    canManage: schedule.canManage
  };
}

function toFrontendScheduleDetail(schedule: ApiScheduleDetail): ScheduleDetail {
  assertApiScheduleDetailRecordsConsistent(schedule);
  const listItem = toFrontendScheduleListItem(schedule);

  return {
    ...listItem,
    deletedAt: schedule.deletedAt,
    attendance: schedule.attendance.enabled
      ? {
          attendanceEventId: schedule.attendance.attendanceEventId,
          category: toFrontendAttendanceCategory(schedule.attendance.attendanceCategory),
          participantMemberGroupIds: schedule.attendance.participantMemberGroups.map(
            (memberGroup) => memberGroup.memberGroupId
          ),
          participantMemberGroups: schedule.attendance.participantMemberGroups.map((memberGroup) => ({
            memberGroupId: memberGroup.memberGroupId,
            name: memberGroup.name
          })),
          onTimeReferenceMinutesFromStart: schedule.attendance.onTimeReferenceMinutesFromStart,
          lateReferenceMinutesFromStart: schedule.attendance.lateReferenceMinutesFromStart,
          currentUserRecord: schedule.attendance.currentUserRecord
            ? toFrontendAttendanceRecord(schedule.attendance.currentUserRecord)
            : null,
          records: schedule.attendance.records.map(toFrontendAttendanceRecord)
        }
      : null
  };
}

function assertApiScheduleDetailRecordsConsistent(schedule: ApiScheduleDetail): void {
  if (!schedule.attendance.enabled || !schedule.attendance.currentUserRecord) {
    return;
  }

  const currentRecordId = schedule.attendance.currentUserRecord.attendanceRecordId;
  const currentRecordExists = schedule.attendance.records.some(
    (record) => record.attendanceRecordId === currentRecordId
  );
  if (!currentRecordExists) {
    throw new Error('Malformed schedule detail response');
  }
}

function toFrontendAssignmentCreateDetail(
  response: ApiAssignmentCreateResponse,
  workspaceContext: WorkspaceContextInput
): AssignmentDetail {
  if (
    response.assignment.choirId !== workspaceContext.currentChoirId ||
    response.assignment.seasonId !== workspaceContext.currentSeasonId
  ) {
    throw new Error('Assignment detail does not match current workspace');
  }

  return {
    assignmentId: response.assignment.assignmentId,
    choirId: response.assignment.choirId,
    seasonId: response.assignment.seasonId,
    pieceName: response.assignment.pieceName,
    dueAt: response.assignment.dueAt,
    submissionType: response.assignment.submissionType,
    description: response.assignment.description,
    status: response.assignment.status,
    mySubmissionStatus: 'not_submitted',
    submittedCount: 0,
    expectedSubmitterCount: 0,
    deliveryRuleCount: response.deliveryRules.length,
    canManage: response.assignment.canManage,
    deliveryRules: response.deliveryRules,
    mySubmission: null,
    submissionRows: [],
    canSubmit: false,
    canOverwrite: false
  };
}

function toFrontendAssignmentDetail(
  response: ApiAssignmentDetailResponse,
  workspaceContext: WorkspaceContextInput
): AssignmentDetail {
  if (
    response.assignment.choirId !== workspaceContext.currentChoirId ||
    response.assignment.seasonId !== workspaceContext.currentSeasonId
  ) {
    throw new Error('Assignment detail does not match current workspace');
  }

  const mySubmission = toFrontendMySubmission(response.mySubmission);

  return {
    assignmentId: response.assignment.assignmentId,
    choirId: response.assignment.choirId,
    seasonId: response.assignment.seasonId,
    pieceName: response.assignment.pieceName,
    dueAt: response.assignment.dueAt,
    submissionType: response.assignment.submissionType,
    description: response.assignment.description,
    status: response.assignment.status,
    mySubmissionStatus: mySubmission.status,
    submittedCount: response.publicSubmissionStatus.filter((row) => row.status === 'submitted').length,
    expectedSubmitterCount: response.publicSubmissionStatus.length,
    deliveryRuleCount: response.deliveryRules.length,
    canManage: response.assignment.canManage,
    deliveryRules: response.deliveryRules,
    mySubmission: mySubmission.submission,
    submissionRows: response.publicSubmissionStatus.map((row) => ({
      seasonMembershipId: row.seasonMembershipId,
      nickname: row.nickname,
      memberGroupNames: row.memberGroupNames,
      status: row.status,
      submittedAt: row.submittedAt
    })),
    canSubmit: response.submissionEntry.canSubmit,
    canOverwrite: response.submissionEntry.isOverwrite
  };
}

function toFrontendMySubmission(payload: ApiAssignmentDetailResponse['mySubmission']): {
  readonly status: AssignmentListItem['mySubmissionStatus'];
  readonly submission: AssignmentDetail['mySubmission'];
} {
  if (payload.status !== 'submitted') {
    return {
      status: payload.status,
      submission: null
    };
  }

  if (!isSubmittedAssignmentPayload(payload)) {
    throw new Error('Assignment detail submitted payload is invalid');
  }

  return {
    status: payload.status,
    submission: {
      assignmentSubmissionId: payload.assignmentSubmissionId,
      submittedAt: payload.submittedAt,
      fileName: payload.fileName
    }
  };
}

function isSubmittedAssignmentPayload(
  payload: ApiAssignmentDetailResponse['mySubmission']
): payload is Extract<ApiAssignmentDetailResponse['mySubmission'], { readonly assignmentSubmissionId: string }> {
  return (
    'assignmentSubmissionId' in payload &&
    typeof payload.assignmentSubmissionId === 'string' &&
    typeof payload.submittedAt === 'string' &&
    typeof payload.fileName === 'string'
  );
}

function toFrontendCleanupWarning(cleanupWarning: AssignmentSubmissionResponse['cleanupWarning']): string | null {
  if (cleanupWarning === null) {
    return null;
  }

  if (typeof cleanupWarning.objectKey !== 'string' || typeof cleanupWarning.message !== 'string') {
    throw new Error('Assignment cleanup warning payload is invalid');
  }

  return cleanupWarning.message;
}

function toFrontendAttendanceCategory(category: ApiScheduleAttendanceCategory) {
  return {
    attendanceCategoryId: category.attendanceCategoryId,
    name: category.name
  };
}

function toFrontendAttendanceRecord(record: ApiAttendanceRecord) {
  return {
    attendanceRecordId: record.attendanceRecordId,
    seasonMembershipId: record.seasonMembershipId,
    nickname: record.nickname,
    memberGroupNames: record.memberGroupNames,
    status: record.status,
    selectedAt: record.selectedAt,
    settledAt: record.settledAt
  };
}

function assertSupportedMemberSelectorPurpose(purpose: MemberSelectorPurpose): void {
  if (
    purpose !== 'member_group_initial_member' &&
    purpose !== 'member_group_add_member' &&
    purpose !== 'member_selection'
  ) {
    throw new Error('Unsupported member selector purpose');
  }
}
